import { smsTemplate } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * SMS Template Service
 * Handles all SMS template-related API calls.
 * 
 * API Endpoints:
 * - GET /api/v1/admin/sms-template/list - Get SMS template list
 * - GET /api/v1/admin/sms-template/get - Get single SMS template
 * - POST /api/v1/admin/sms-template/add - Add new SMS template
 * - PUT /api/v1/admin/sms-template/update - Update SMS template
 * - DELETE /api/v1/admin/sms-template/delete - Delete SMS template
 */
class SmsTemplateService {
  /**
   * @name getList
   * @description Fetches SMS template list from admin API with pagination and search.
   * @param {Object} params - Query parameters (page, limit, search_text)
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data containing the SMS template list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(smsTemplate.list, { params });
      return response.data;
    } catch (err) {
      throw err; // Re-throw the error for the caller to handle
    }
  }

  /**
   * @name get
   * @description Fetches a single SMS template by UUID.
   * @param {string} uuid - SMS Template UUID
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data containing SMS template details.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(smsTemplate.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name add
   * @description Creates a new SMS template.
   * @param {Object} data - SMS template data with slug, parameter, and templates array
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async add(data) {
    try {
      const response = await axios.post(smsTemplate.add, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name update
   * @description Updates an existing SMS template.
   * @param {Object} data - SMS template data with uuid, slug, parameter, and templates array
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async update(data) {
    try {
      const response = await axios.put(smsTemplate.update, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name delete
   * @description Deletes an SMS template by UUID.
   * @param {string} uuid - SMS Template UUID
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async delete(uuid) {
    try {
      const response = await axios.delete(smsTemplate.delete, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { SmsTemplateService };
